import {
  UpdateEntitiesOverviewBundleConfigurationEntityInput,
  UpdateEntitiesOverviewBundleConfigurationInput,
} from '@/types/schema';

import { NEW_ROW_ID_SENTINEL } from '../../ClientPresentationDesigner.constants';
import {
  isEmptyKeyedListMutationOperations,
  mapKeyedListMutationOperations,
} from '../diff/mapKeyedListMutationOperations';
import {
  BundleConfigurationPayload,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';

/** Minimal row shape used by the entities overview diff. */
export interface EntitiesOverviewRow {
  id: string;
  index: number;
  isHidden: boolean;
  entity: { id: string };
}

function serverRowId(row: { id: string }): string | null {
  return row.id.startsWith(NEW_ROW_ID_SENTINEL) ? null : row.id;
}

export function diffEntitiesOverviewRows(
  previous: EntitiesOverviewRow[],
  next: EntitiesOverviewRow[],
  householdId: string
) {
  const previousPositionById = new Map(
    previous.map((row, index) => [row.id, index])
  );

  return mapKeyedListMutationOperations({
    previousItems: previous,
    currentItems: next,
    getId: serverRowId,
    toCreate: (row, sortOrder) => ({
      create: {
        householdID: householdId,
        entityID: row.entity.id,
        index: sortOrder,
        isHidden: row.isHidden,
      },
    }),
    toUpdate: ({ previous: was, next: is, sortOrder }) => {
      const row: UpdateEntitiesOverviewBundleConfigurationEntityInput = {};
      if (previousPositionById.get(is.id) !== sortOrder) {
        row.index = sortOrder;
      }
      if (was.isHidden !== is.isHidden) row.isHidden = is.isHidden;
      return Object.keys(row).length > 0 ? { id: is.id, update: row } : null;
    },
  });
}

export function diffEntitiesOverviewBundleConfiguration({
  previous,
  next,
  context,
}: DiffBundleConfigurationArgs): BundleConfigurationPayload | null {
  const before = previous.entitiesOverviewConfiguration;
  const after = next.entitiesOverviewConfiguration;
  if (!after) return null;

  if (!before) {
    return {
      withEntitiesOverviewBundleConfiguration: {
        create: {
          householdID: context.householdId,
          displayName: after.displayName,
          showSummaryMetrics: after.showSummaryMetrics,
        },
        withEntities: after.entities.map((row, index) => ({
          create: {
            householdID: context.householdId,
            entityID: row.entity.id,
            index: row.index ?? index,
            isHidden: row.isHidden,
          },
        })),
      },
    };
  }

  if (!after.id) return null;

  const update: UpdateEntitiesOverviewBundleConfigurationInput = {};
  if (before.displayName !== after.displayName) {
    if (after.displayName) {
      update.displayName = after.displayName;
    } else {
      update.clearDisplayName = true;
    }
  }
  if (before.showSummaryMetrics !== after.showSummaryMetrics) {
    update.showSummaryMetrics = after.showSummaryMetrics;
  }

  const rows = diffEntitiesOverviewRows(
    before.entities,
    after.entities,
    context.householdId
  );

  if (rows.removeIDs.length > 0) update.removeEntityIDs = rows.removeIDs;
  if (
    Object.keys(update).length === 0 &&
    isEmptyKeyedListMutationOperations(rows)
  ) {
    return null;
  }

  return {
    updateEntitiesOverviewBundleConfiguration: {
      id: after.id,
      update,
      ...(rows.creates.length > 0 ? { withEntities: rows.creates } : {}),
      ...(rows.updates.length > 0 ? { updateEntities: rows.updates } : {}),
    },
  };
}
